import React, { useEffect, useState } from "react";
import { Routes, Route, useLocation, useNavigate, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./lib/auth";
import api from "./lib/api";

import LoginPage from "./pages/Login";
import RosterPage from "./pages/Roster";
import WrestlerPage from "./pages/Wrestler";
import ShopPage from "./pages/Shop";
import TrainingPage from "./pages/Training";
import PromoPage from "./pages/Promo";
import PvPPage from "./pages/PvP";
import LeaderboardPage from "./pages/Leaderboard";
import AchievementsPage from "./pages/Achievements";
import CardsPage from "./pages/Cards";
import NameSetup from "./pages/NameSetup";
import ProfilePage from "./pages/Profile";
import PromotionPage from "./pages/Promotion";
import InboxPage from "./pages/Inbox";
import TagTeamPage from "./pages/TagTeam";
import PyroPage from "./pages/Pyro";
import TourPage from "./pages/Tour";
import HOFPage from "./pages/HOF";
import { PrivacyPolicy, TermsOfService, LegalNotice } from "./pages/Legal";
import TopMenu from "./components/TopMenu";
import MobileZoom from "./components/MobileZoom";
import AdminPage from "./pages/Admin";
import LockedScreen from "./components/LockedScreen";
import AchievementUnlockPopup from "./components/AchievementUnlockPopup";
import { Toaster, toast } from "sonner";

function AuthCallback() {
  const nav = useNavigate();
  const { refresh, setLocked } = useAuth();
  const [err, setErr] = useState("");
  const hasProcessed = React.useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;
    const hash = window.location.hash;
    const m = hash.match(/session_id=([^&]+)/);
    if (!m) { nav("/"); return; }
    const sessionId = decodeURIComponent(m[1]);
    let upgrading = false;
    try { upgrading = localStorage.getItem("rk_guest_upgrade") === "1"; localStorage.removeItem("rk_guest_upgrade"); } catch {}
    if (upgrading) {
      api.post("/auth/upgrade/google", { session_id: sessionId })
        .then(() => toast.success("Progress saved! Your account is now linked to Google."))
        .catch((e) => toast.error(e.response?.data?.detail || "Could not save with Google"))
        .finally(async () => {
          window.history.replaceState({}, "", window.location.pathname);
          await refresh();
          nav("/roster", { replace: true });
        });
      return;
    }
    api.post("/auth/session", { session_id: sessionId })
      .then(async (res) => {
        // Clean hash
        window.history.replaceState({}, "", window.location.pathname);
        // Attach pending referral code (one-time) if present.
        try {
          const ref = localStorage.getItem("pending_referral_code");
          if (ref) {
            await api.post("/referral/attach", { code: ref }).catch(() => {});
            localStorage.removeItem("pending_referral_code");
          }
        } catch {}
        await refresh();
        if (res.data.needs_name) nav("/setup-name", { replace: true });
        else nav("/roster", { replace: true });
      })
      .catch((e) => {
        const d = e.response?.data?.detail;
        if (d?.code === "account_locked") { window.history.replaceState({}, "", "/"); setLocked(d); return; }
        setErr(typeof d === "string" ? d : "Auth failed");
      });
  }, [nav, refresh, setLocked]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#0A051A]">
      <div className="text-center">
        <div className="font-heading text-3xl neon-pink mb-4">SIGNING IN...</div>
        <div className="font-mono text-[#A89BCC] text-sm">Connecting to the ring</div>
        {err && <div className="mt-4 text-red-400 font-mono text-sm">{err}</div>}
      </div>
    </div>
  );
}

function Protected({ children }) {
  const { user, loading } = useAuth();
  if (loading) return (
    <div className="min-h-screen flex items-center justify-center"><div className="font-heading text-2xl neon-cyan">LOADING...</div></div>
  );
  if (!user) return <Navigate to="/" replace />;
  if (!user.account_name) return <Navigate to="/setup-name" replace />;
  return children;
}

function AppShell() {
  const location = useLocation();
  const { locked } = useAuth();
  if (locked) return <LockedScreen />;
  if (location.hash?.includes("session_id=")) return <AuthCallback />;

  return (
    <>
      <TopMenu />
      <AchievementUnlockPopup />
      <Routes>
        <Route path="/" element={<LoginPage />} />
        <Route path="/setup-name" element={<NameSetup />} />
        <Route path="/roster" element={<Protected><RosterPage /></Protected>} />
        <Route path="/profile" element={<Protected><ProfilePage /></Protected>} />
        <Route path="/profile/:account_name" element={<Protected><ProfilePage /></Protected>} />
        <Route path="/promotion" element={<Protected><PromotionPage /></Protected>} />
        <Route path="/promotion/:pid" element={<Protected><PromotionPage /></Protected>} />
        <Route path="/inbox" element={<Protected><InboxPage /></Protected>} />
        <Route path="/inbox/:account_name" element={<Protected><InboxPage /></Protected>} />
        <Route path="/wrestler/:id" element={<Protected><WrestlerPage /></Protected>} />
        <Route path="/wrestler/:id/training" element={<Protected><MobileZoom width={1024} unitHeight={860}><TrainingPage /></MobileZoom></Protected>} />
        <Route path="/wrestler/:id/promo" element={<Protected><PromoPage /></Protected>} />
        <Route path="/shop" element={<Protected><ShopPage /></Protected>} />
        <Route path="/pvp" element={<Protected><PvPPage /></Protected>} />
        <Route path="/pyro" element={<Protected><MobileZoom width={1280} unitHeight={760}><PyroPage /></MobileZoom></Protected>} />
        <Route path="/tour" element={<Protected><MobileZoom width={1260} unitHeight={900}><TourPage /></MobileZoom></Protected>} />
        <Route path="/hof" element={<Protected><HOFPage /></Protected>} />
        <Route path="/tag-team/:team_id" element={<Protected><TagTeamPage /></Protected>} />
        <Route path="/leaderboard" element={<Protected><LeaderboardPage /></Protected>} />
        <Route path="/cards" element={<Protected><CardsPage /></Protected>} />
        <Route path="/achievements" element={<Protected><AchievementsPage /></Protected>} />
        <Route path="/admin" element={<Protected><AdminPage /></Protected>} />
        <Route path="/privacy" element={<PrivacyPolicy />} />
        <Route path="/terms" element={<TermsOfService />} />
        <Route path="/legal" element={<LegalNotice />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </>
  );
}

export default function App() {
  return (
    <div className="App">
      <AuthProvider>
        <AppShell />
        <Toaster position="top-right" theme="dark" toastOptions={{
          style: { background: '#12082B', border: '1px solid #FF007F', color: 'white' },
        }} />
      </AuthProvider>
    </div>
  );
}
